import { createUseStyles } from 'react-jss';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import { TextColors } from '@picsart/design-system/foundation/textColors';
import { PrimaryColors } from '@picsart/design-system/foundation/primaryColors';
import { FontWeights } from '@picsart/design-system/foundation/fonts';
import { FontSizes } from '@picsart/design-system/foundation/typography';
import { textOverflowHidden } from '@picsart/design-system/styleHelpers/textOverflowHidden';
import { BreakpointValues } from '@picsart/design-system/foundation/responsive';

const useStyles = createUseStyles(
  {
    left: {
      display: 'flex',
      alignItems: 'center',
      gap: Spacings.s12,
      flex: 1,
      minWidth: 0, // Allow shrinking below content size
      overflow: 'hidden',
    },
    // Logo styles
    logo: {
      width: 32,
      height: 32,
      flexShrink: 0,
      borderRadius: CornerRadius.r4,
      [`@media (max-width: ${BreakpointValues.XS}px)`]: {
        width: 28,
        height: 28,
      },
    },
    // Hide logo on mobile for non-library screens (shows back button + title only)
    logoHiddenOnMobile: {
      [`@media (max-width: ${BreakpointValues.SM - 1}px)`]: {
        display: 'none',
      },
    },
    titleGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: Spacings.s12,
      minWidth: 0, // Allow shrinking
      overflow: 'hidden',
      [`@media (max-width: ${BreakpointValues.XS}px)`]: {
        gap: Spacings.s8,
      },
    },
    // Wrapper modifier for DS Divider — owns positioning/visibility only.
    titleDivider: {
      flexShrink: 0,
      // Hide on small mobile
      [`@media (max-width: ${BreakpointValues.XS}px)`]: {
        display: 'none',
      },
    },
    // Mobile-only wrapper - hides the back InlineButton on desktop via CSS
    backButton: {
      display: 'inline-flex',
      // Hide on desktop - CSS handles responsive
      [`@media (min-width: ${BreakpointValues.SM}px)`]: {
        display: 'none',
      },
    },
    iconButton: {
      width: Spacings.s40,
      height: Spacings.s40,
      flex: `0 0 ${Spacings.s40}`,
      borderRadius: CornerRadius.circle,
    },
    title: {
      fontSize: FontSizes.s16,
      fontWeight: FontWeights.bold,
      color: TextColors.base,
      margin: 0,
      whiteSpace: 'nowrap',
      flexShrink: 0, // Title never truncates
      [`@media (max-width: ${BreakpointValues.XS}px)`]: {
        fontSize: FontSizes.s14,
      },
    },
    // Desktop title - shown only on desktop
    titleDesktop: {
      display: 'none',
      [`@media (min-width: ${BreakpointValues.SM}px)`]: {
        display: 'block',
      },
    },
    // Mobile title - hidden on desktop
    titleMobile: {
      display: 'block',
      [`@media (min-width: ${BreakpointValues.SM}px)`]: {
        display: 'none',
      },
    },
    // Tagline with gradient text (pink to purple, matching app buttons)
    tagline: {
      fontSize: FontSizes.s14,
      fontWeight: FontWeights.semibold,
      margin: 0,
      ...textOverflowHidden,
      // Gradient text effect - pink to purple
      background: `linear-gradient(90deg, ${PrimaryColors.base.default} 0%, ${PrimaryColors.base.action} 100%)`,
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      // Hide on small mobile to keep header to 1 line
      [`@media (max-width: ${BreakpointValues.XS}px)`]: {
        display: 'none',
      },
    },
    // Tagline shown on desktop only
    taglineDesktop: {
      display: 'none',
      [`@media (min-width: ${BreakpointValues.SM}px)`]: {
        display: 'block',
      },
    },
    // Tagline shown on tablet/medium mobile, hidden on small mobile and desktop
    taglineMobile: {
      display: 'block',
      [`@media (min-width: ${BreakpointValues.SM}px)`]: {
        display: 'none',
      },
      // Hide on small mobile to keep header to 1 line
      [`@media (max-width: ${BreakpointValues.XS}px)`]: {
        display: 'none',
      },
    },
  },
  { name: 'header-left' }
);

export default useStyles;
